import { Navigate, Route, Routes } from 'react-router';

import { AccountPage } from '@/features/account/AccountPage';
import { AdminIntegrationsPage } from '@/features/admin/AdminIntegrationsPage';
import { AdminNewTenantPage } from '@/features/admin/AdminNewTenantPage';
import { AdminPage } from '@/features/admin/AdminPage';
import { AdminTenantPage } from '@/features/admin/AdminTenantPage';
import { AdminTemplatesPage } from '@/features/admin/AdminTemplatesPage';
import { AdminTenantsPage } from '@/features/admin/AdminTenantsPage';
import { AdminUserPage } from '@/features/admin/AdminUserPage';
import { AdminUsersPage } from '@/features/admin/AdminUsersPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { InvitationPage } from '@/features/auth/InvitationPage';
import { LegacyLoginRedirect } from '@/features/auth/LegacyLoginRedirect';
import { LoginPage } from '@/features/auth/LoginPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { DossierPage } from '@/features/dossier/DossierPage';
import { InspectionsPage } from '@/features/inspections/InspectionsPage';
import { MyDayPage } from '@/features/inspections/MyDayPage';
import { NotFoundPage } from '@/features/shell/NotFoundPage';
import { PlanningPage } from '@/features/planning/PlanningPage';
import { LegacyPublicReportRedirect } from '@/features/public-report/LegacyPublicReportRedirect';
import { PublicReportPage } from '@/features/public-report/PublicReportPage';
import { PublicReportPreviewPage } from '@/features/public-report/PublicReportPreviewPage';
import { ReportPage } from '@/features/report/ReportPage';
import { ReportViewPage } from '@/features/report/ReportViewPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { EnergyPerformancePage } from '@/features/survey/EnergyPerformancePage';
import { SurveyPage } from '@/features/survey/SurveyPage';

import { SessionScope } from '../providers/SessionCacheGuard';
import { AppShell } from '../layouts/AppShell';
import { OrderLayout } from '../layouts/OrderLayout';
import { PublicLayout } from '../layouts/PublicLayout';
import { RedirectIfAuthenticated, RequireAuth, RequireRole, RequireWorkspace } from './guards';
import { patterns } from './paths';
import { PLATFORM_ROLES, routeRoles } from './roles';

/**
 * Alle schermen uit het ontwerp.
 *
 * Er is één schil voor alles achter het inlogscherm - `AppShell`, met de
 * navigatierail. Ook het platformbeheer draait daarin: een beheerder krijgt daar
 * de groep PLATFORM in de rail bij, in plaats van een eigen tweede schil.
 */
export function AppRouter() {
    return (
        /* Andere persoon, ander bedrijf of meekijken: alle schermen beginnen opnieuw. */
        <SessionScope>
            <Routes>
                {/* Publiek: de klantpagina heeft geen sessie nodig, alleen een token. */}
                <Route element={<PublicLayout />}>
                    <Route path={patterns.publicReport} element={<PublicReportPage />} />
                    <Route
                        path={patterns.legacyPublicReport}
                        element={<LegacyPublicReportRedirect />}
                    />
                </Route>

                <Route
                    path={patterns.login}
                    element={
                        <RedirectIfAuthenticated>
                            <LoginPage />
                        </RedirectIfAuthenticated>
                    }
                />
                <Route path={patterns.legacyLogin} element={<LegacyLoginRedirect />} />

                {/* Ook publiek: wie hier komt heeft (nog) geen sessie, of juist die van
                iemand anders op dit apparaat. */}
                <Route path={patterns.forgotPassword} element={<ForgotPasswordPage />} />
                <Route path={patterns.resetPassword} element={<ResetPasswordPage />} />
                <Route path={patterns.invitation} element={<InvitationPage />} />
                {/* Dezelfde uitnodiging, als welkom na een aanmelding op de website. */}
                <Route
                    path={patterns.activation}
                    element={<InvitationPage variant="activation" />}
                />

                <Route
                    element={
                        <RequireWorkspace>
                            <AppShell />
                        </RequireWorkspace>
                    }
                >
                    <Route path={patterns.dashboard} element={<DashboardPage />} />

                    <Route
                        path={patterns.myDay}
                        element={
                            <RequireRole roles={routeRoles.myDay}>
                                <MyDayPage />
                            </RequireRole>
                        }
                    />

                    <Route
                        path={patterns.planning}
                        element={
                            <RequireRole roles={routeRoles.planning}>
                                <PlanningPage />
                            </RequireRole>
                        }
                    />

                    <Route path={patterns.inspections} element={<InspectionsPage />} />

                    {/* Eén keuring: één kop met de weg terug, wat deze keuring is en haar
                    tabbladen. De schermen eronder hebben geen eigen kop. */}
                    <Route element={<OrderLayout />}>
                        <Route path={patterns.dossier} element={<DossierPage />} />
                        <Route path={patterns.report} element={<ReportPage />} />

                        {/* Wat de opdrachtgever ziet, bekeken vanaf kantoor: dezelfde
                        pagina, met linkstatistieken en toegangsinstellingen ernaast. */}
                        <Route path={patterns.clientPage} element={<PublicReportPreviewPage />} />
                    </Route>

                    {/* Opname en energielabel zijn werkplekken over de volle hoogte, zonder
                    de kop van de keuring: hun eigen compacte balk leidt terug. */}
                    <Route path={patterns.survey} element={<SurveyPage />} />
                    <Route path={patterns.energyPerformance} element={<EnergyPerformancePage />} />

                    {/* Het rapport zoals de koper het ziet, over het hele scherm. Ook dit
                    hangt buiten de kop met tabbladen: die nam de halve hoogte. */}
                    <Route path={patterns.reportView} element={<ReportViewPage />} />

                    <Route
                        path={patterns.billing}
                        element={
                            <RequireRole roles={routeRoles.billing}>
                                <BillingPage />
                            </RequireRole>
                        }
                    />

                    <Route
                        path={patterns.settings}
                        element={
                            <RequireRole roles={routeRoles.settings}>
                                <SettingsPage />
                            </RequireRole>
                        }
                    />
                </Route>

                {/* Je eigen account hoort bij jou, niet bij een bedrijf: ook zonder omgeving. */}
                <Route
                    element={
                        <RequireAuth>
                            <AppShell />
                        </RequireAuth>
                    }
                >
                    <Route path={patterns.account} element={<AccountPage />} />
                </Route>

                <Route
                    element={
                        <RequireAuth>
                            <RequireRole roles={PLATFORM_ROLES}>
                                <AppShell />
                            </RequireRole>
                        </RequireAuth>
                    }
                >
                    <Route path={patterns.admin} element={<AdminPage />} />
                    <Route path={patterns.adminAudit} element={<AdminPage view="audit" />} />
                    <Route path={patterns.adminIntegrations} element={<AdminIntegrationsPage />} />
                    <Route path={patterns.adminTenants} element={<AdminTenantsPage />} />
                    <Route path={patterns.adminNewTenant} element={<AdminNewTenantPage />} />
                    <Route
                        path={patterns.adminCredits}
                        element={<Navigate to={patterns.adminTenants} replace />}
                    />
                    <Route path={patterns.adminTenant} element={<AdminTenantPage />} />
                    <Route path={patterns.adminUsers} element={<AdminUsersPage />} />
                    <Route path={patterns.adminUser} element={<AdminUserPage />} />
                    <Route path={patterns.adminTemplates} element={<AdminTemplatesPage />} />
                </Route>

                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </SessionScope>
    );
}
